import { Controller } from '@hotwired/stimulus'

/**
 * Section View Controller
 * Fires an Ahoy `lp_section_viewed` event the first time this element
 * enters the viewport, then stops observing.
 *
 * Purely an analytics hook — it has no visual side effect at all, unlike
 * reveal_controller.js (which must fail *visible*). This one must fail
 * *silent*: if IntersectionObserver or ahoy is unavailable, it does
 * nothing and never touches the DOM.
 *
 * `ahoy` is checked at fire time (inside the observer callback), not at
 * connect() time. `ahoy.js` loads via a `defer`d <script> tag
 * (layouts/landing.html.erb), and a non-async module script — which is
 * what Stimulus itself boots from (vite_javascript_tag) — always executes
 * before a later classic `defer` script, per the HTML spec's script
 * ordering rules. So `typeof ahoy` is guaranteed `'undefined'` at every
 * controller's connect() time, on every page load, in every browser — not
 * an occasional race. Checking only `IntersectionObserver` here (a real
 * capability check, not a load-order-dependent one) and deferring the
 * `ahoy` check to the moment the section actually scrolls into view — by
 * which point the deferred script has long since run — is what makes this
 * fire at all. See docs/marketing/ppc-landing-pages.md's Funnel analytics
 * section for the full incident writeup.
 *
 * `threshold: 0` — fires as soon as any part of the section intersects,
 * not once a fraction of it is simultaneously on screen. The question this
 * event answers is "did this section come into view," not "was N% of it
 * visible at once" — and the latter is a threshold a section taller than
 * the viewport can never satisfy at all, at any scroll position. (Med
 * spa's `showcase` section, seven stacked cards on a phone, is ~4x an
 * 844px viewport's height — no ratio-based threshold above ~24% could ever
 * fire for it there.) `rootMargin: '0px 0px -8% 0px'` shrinks the
 * intersection root by 8% of the viewport at the bottom edge, so a
 * one-pixel sliver scrolling past the very bottom of the screen doesn't
 * count — same precedent and same value reveal_controller.js already uses
 * for its own fade-in trigger.
 *
 *   <section data-controller="public--section-view"
 *             data-public--section-view-section-value="showcase">
 */
export default class extends Controller {
  static values = {
    section: String
  }

  connect() {
    if (typeof IntersectionObserver === 'undefined') return

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        this.trackView()
        this.observer.unobserve(entry.target)
      })
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' })

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  trackView() {
    if (typeof ahoy === 'undefined') return

    ahoy.track('lp_section_viewed', {
      section: this.sectionValue,
      page: window.location.pathname
    })
  }
}
